const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

export const DEMO_ROLE_IDS = new Set([3, 4]);

export const emptyInfrastructureForm = {
  nom_infrastructure: "",
  adresse_infrastructure: "",
  description_infrastructure: "",
  photo_url1: "",
  photo_url2: "",
  gps_infrastructure: "",
  ordre_infrastructure: 1,
};

export function extractArray(data) {
  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.infrastructures)) return data.infrastructures;
    if (Array.isArray(data.data)) return data.data;
  }

  return [];
}

export function normalizeText(value = "") {
  return String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function isExternalURL(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function resolveMediaUrl(
  url,
  fallback = "/images/infrastructure_placeholder.jpg",
) {
  if (!url) return fallback;

  const value = String(url).trim();

  if (
    isExternalURL(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (value.startsWith("/")) {
    return `${API_BASE}${value}`;
  }

  return value;
}

export function getCurrentStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

export function isDemoMode() {
  const user = getCurrentStoredUser();
  return DEMO_ROLE_IDS.has(Number(user?.role_id));
}

export function hasGps(infrastructure) {
  const value = String(infrastructure?.gps_infrastructure || "").trim();
  if (!value.includes(",")) return false;

  const [lat, lng] = value.split(",").map((coord) => Number(coord.trim()));

  return Number.isFinite(lat) && Number.isFinite(lng);
}

export function getInfrastructurePhotos(infrastructure) {
  return [
    infrastructure?.photo_url1,
    infrastructure?.photo_url2,
    infrastructure?.photo_url3,
    infrastructure?.photo_url4,
  ].filter(Boolean);
}

export function getStats(infrastructures) {
  return {
    total: infrastructures.length,
    withGps: infrastructures.filter(hasGps).length,
    withPhotos: infrastructures.filter(
      (item) => getInfrastructurePhotos(item).length > 0,
    ).length,
    withoutGps: infrastructures.filter((item) => !hasGps(item)).length,
  };
}

export function truncateText(value = "", max = 120) {
  const text = String(value || "").trim();

  if (text.length <= max) return text;

  return `${text.slice(0, max).trim()}...`;
}

export function mapInfrastructureToForm(infrastructure = {}) {
  return {
    nom_infrastructure: infrastructure.nom_infrastructure || "",
    adresse_infrastructure: infrastructure.adresse_infrastructure || "",
    description_infrastructure: infrastructure.description_infrastructure || "",
    photo_url1: infrastructure.photo_url1 || "",
    photo_url2: infrastructure.photo_url2 || "",
    gps_infrastructure: infrastructure.gps_infrastructure || "",
    ordre_infrastructure: infrastructure.ordre_infrastructure ?? 1,
  };
}

export function buildInfrastructurePayload(formData) {
  return {
    nom_infrastructure: formData.nom_infrastructure,
    adresse_infrastructure: formData.adresse_infrastructure,
    description_infrastructure: formData.description_infrastructure,
    photo_url1: formData.photo_url1,
    photo_url2: formData.photo_url2,
    gps_infrastructure: formData.gps_infrastructure,
    ordre_infrastructure: Number(formData.ordre_infrastructure || 1),
  };
}

export function buildGoogleMapLink(infrastructure) {
  const gps = String(infrastructure?.gps_infrastructure || "").trim();

  if (gps.includes(",")) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      gps,
    )}`;
  }

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    infrastructure?.adresse_infrastructure || "Le Bouscat 33110",
  )}`;
}

export function buildInfrastructureFormData(formData, photoFile1, photoFile2) {
  const payload = new FormData();

  payload.append("nom_infrastructure", formData.nom_infrastructure || "");
  payload.append(
    "adresse_infrastructure",
    formData.adresse_infrastructure || "",
  );
  payload.append(
    "description_infrastructure",
    formData.description_infrastructure || "",
  );
  payload.append(
    "ordre_infrastructure",
    String(formData.ordre_infrastructure || 1),
  );
  payload.append("gps_infrastructure", formData.gps_infrastructure || "");

  payload.append("photo_url1", formData.photo_url1 || "");
  payload.append("photo_url2", formData.photo_url2 || "");

  if (photoFile1) {
    payload.set("photo_url1", photoFile1);
  }

  if (photoFile2) {
    payload.set("photo_url2", photoFile2);
  }

  return payload;
}
